import {
  ChangeDetectorRef,
  Component,
  Input,
  SimpleChanges,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BaseListCtl, ServiceLocatorService } from '@ncs/ncs-lib';

@Component({
  selector: 'app-application-access',
  templateUrl: './application-access.component.html',
  styleUrls: ['./application-access.component.scss'],
})
export class ApplicationAccessComponent extends BaseListCtl {
  @Input() curApp: any;

  applicationsroles: any[] = [];
  accessTypes: string[] = [
    'Role',
    'Role Access',
    'Menu',
    'App Users'
  ];
  availableAccessTypes: string[] = [];
  saveAccessList: string[] = [];
  appliationRoleName: any;

  // NEW: Store permissions for each access type
  accessPermissions: any = {};

  // We'll use form.data.accessType directly
  accessList: string[] = [];

  constructor(
    public override locator: ServiceLocatorService,
    public override route: ActivatedRoute,
    private cdr: ChangeDetectorRef
  ) {
    super(locator.config.get('endpoint.application.role'), locator, route);
    this.form.data['applicationId'] = 0;
  }

  /* ===================== INIT ===================== */
  override ngOnInit() {
    this.saveAccessList = [];
    this.accessPermissions = {};
    this.search();
    this.getApplicationRole();
    this.availableAccessTypes = [...this.accessTypes];
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['curApp'] && this.curApp && changes['curApp']?.currentValue) {
        this.resetForm();
      this.accessList = [];
      this.saveAccessList = [];
      this.accessPermissions = {};
      this.search();
      this.getApplicationRole();
      this.availableAccessTypes = [...this.accessTypes];
     }
  }

  /* ===================== API ===================== */
  getApplicationRole() {
    if (!this.curApp?.id) return;

    const url =
      this.locator.getEndpoint('endpoint.application.role.usecase') +
      '/application-roles/' +
      this.curApp.id;

    this.locator.http.get(url, (res: any, error: boolean) => {
      if (error) return;
      this.applicationsroles = res.result.data;
      this.cdr.detectChanges();
    });
  }

  override search(): void {
    if (this.curApp?.id) {
      this.form.searchParams['applicationId'] = Number(this.curApp.id);
      super.search();
    }
  }

  /* ===================== UI LOGIC ===================== */
  onRoleChange() {
    this.saveAccessList = [];
    this.accessPermissions = {};
    // When role changes, reset accessType and saveAccessList
    this.form.data.accessType = undefined;
    this.loadRoleAccessList();
  }

  loadRoleAccessList() {
    const roleId = Number(this.form.data.applicationRoleId);
    const role = this.form.list.find((item: any) => Number(item.id) === roleId);

    if (!role) {
      console.warn('Role not found in form.list for id', roleId);
      this.saveAccessList = [];
      this.accessPermissions = {};
    } else {
      // Merge role's existing access list with current saveAccessList
      this.saveAccessList = Array.from(
        new Set([...(this.saveAccessList || []), ...(role.accessList || [])])
      );

      // Load permissions if exists
      if (role.accessPermissions) {
        this.accessPermissions = { ...role.accessPermissions };
      } else {
        this.accessPermissions = {};
      }
     this.getApplicationRoleName();
    }

    // Update availableAccessTypes to exclude already selected
    this.availableAccessTypes = this.accessTypes.filter(
      (type) => !this.saveAccessList.includes(type)
    );

    console.log('Loaded saveAccessList for role:', this.saveAccessList);
    console.log('Loaded permissions:', this.accessPermissions);
    console.log('Available access types:', this.availableAccessTypes);

    this.updateAvailableAccessTypes();
  }

  updateAvailableAccessTypes() {
    this.availableAccessTypes = this.accessTypes.filter(
      (type) => !this.saveAccessList.includes(type)
    );
  }

  addAccess() {
    const accessType = this.form.data.accessType;
    const roleId = this.form.data.applicationRoleId;

    if (!roleId || !accessType) return;

    // Check if already exists
    if (this.saveAccessList.includes(accessType)) {
      console.warn('This role already has this access type');
      return;
    }

    // Add to saveAccessList
    this.saveAccessList.push(accessType);

    // Save permissions for this access type
    this.accessPermissions[accessType] = {
      create: this.form.data.create || 0,
      read: this.form.data.read || 0,
      update: this.form.data.update || 0,
      delete: this.form.data.delete || 0,
    };

    // Save immediately
    this.save();

    // Clear form checkboxes
    this.form.data.create = 0;
    this.form.data.read = 0;
    this.form.data.update = 0;
    this.form.data.delete = 0;

    this.cdr.detectChanges();
  }

  removeAccess(index: number) {
    const removedAccess = this.saveAccessList[index];
    const roleId = Number(this.form.data.applicationRoleId);

    const role = this.form.list.find((item: any) => Number(item.id) === roleId);

    if (!role) {
      console.warn('Role not found');
      return;
    }

    // Prepare updated list (but DON'T touch UI yet)
    const updatedAccessList = (role.accessList || []).filter(
      (a: string) => a !== removedAccess
    );

    // Remove permissions for this access type
    const updatedPermissions = { ...this.accessPermissions };
    delete updatedPermissions[removedAccess];

    const payload = {
      ...role,
      accessList: updatedAccessList,
      accessPermissions: updatedPermissions,
    };

    console.log('Attempting delete with payload:', payload);

    // CALL API FIRST
    this.locator.http.post(
      this.locator.getEndpoint('endpoint.application.role') + '/save',
      payload,
      (res: any) => {
        if (res.success) {
          //  API SUCCESS → NOW update UI
          this.saveAccessList = [...updatedAccessList];
          this.accessPermissions = { ...updatedPermissions };
          role.accessList = [...updatedAccessList];
          role.accessPermissions = { ...updatedPermissions };

          this.updateAvailableAccessTypes();
          this.form.data.accessType = undefined;
          this.cdr.detectChanges();
        } else {
          console.error('Delete failed on server');
          alert('Delete failed. Please try again.');
        }
      },
      (error: any) => {
        console.error('API error while deleting access', error);
        alert('Server error. Access not removed.');
      }
    );
  }

  /* ===================== SAVE ===================== */
  override async save() {
    if (!this.curApp?.id) {
      console.error('No current app ID found! curApp:', this.curApp);
      return;
    }

    const roleId = Number(this.form.data.applicationRoleId);
    const role = this.form.list.find((item: any) => Number(item.id) === roleId);

    if (!role) {
      console.warn('Role not found in form.list for id', roleId);
      return;
    }

    // Merge new accessType from form into role.accessList
    const newAccess: string[] = [];

    role.accessList = Array.from(
      new Set([
        ...(role.accessList || []),
        ...newAccess,
        ...this.saveAccessList,
      ])
    );

    // Add permissions to role
    role.accessPermissions = { ...this.accessPermissions };

    // Update saveAccessList in component
    this.saveAccessList = [...role.accessList];

   

    this.locator.http.post(
      this.locator.getEndpoint('endpoint.application.role') + '/save',
      role,
      (res: any) => {

        
        this.form.error = !res.success;
        this.form.message = res.result.message;
        if (res.success) {
        this.form.message = 'Data is saved';
          this.accessList = [];

          let data = res.result.data;
          this.saveAccessList = data.accessList || [];

          // Load permissions if returned from backend
          if (data.accessPermissions) {
            this.accessPermissions = { ...data.accessPermissions };
          }

          this.form.data.applicationRoleId = data.id;

          this.updateAvailableAccessTypes();
          this.cdr.detectChanges();
        } else {
          console.error('Save failed! Response:', res);
        }
      },
      (error: any) => {
        console.error('API error:', error);
      }
    );
  }

  override resetForm() {
    super.resetForm();
    this.form.data['applicationId'] = 0;
    this.form.data['applicationRoleId'] = undefined;
    this.form.data['accessType'] = undefined;
    this.form.data['create'] = 0;
    this.form.data['read'] = 0;
    this.form.data['update'] = 0;
    this.form.data['delete'] = 0;
  }

  async convertStringToNumber(field: string): Promise<void> {
    if (this.form.data[field]) {
      this.form.data[field] = +this.form.data[field];
    }
  }

  convertBooleanToNumber(field: string) {
    this.form.data[field] = this.form.data[field] ? 1 : 0;
  }

  // Helper method to get permission value (optional, for template)
  getPermission(accessType: string, permission: string): number {
    if (this.accessPermissions[accessType]) {
      return this.accessPermissions[accessType][permission.toLowerCase()] || 0;
    }
    return 0;
  }

  getPermissionValue(accessType: string, permission: string): number {
    if (this.accessPermissions && this.accessPermissions[accessType]) {
      return this.accessPermissions[accessType][permission.toLowerCase()] || 0;
    }
    return 0;
  }

  getApplicationRoleName() {
    const roleId = Number(this.form.data.applicationRoleId);
    if (!roleId) return ;

    const role = this.applicationsroles.find((r) => Number(r.id) === roleId);
    this.appliationRoleName= role ? role.name : '';
  }
}
